import { isDangerSign } from '@shared/clinical-logic/helpers/dangerSigns';
import { RISK_SEVERITIES } from '@/utils/riskSeverities';

export type ScoredAssessment = {
  name: string;
  severity: string;
  triage_recommendation: string;
};

/*  A danger sign that scored positive: the worker cannot untick it, so it always carries
    its own referral into the care plan. */
export const isPositiveDangerSign = (assessment: ScoredAssessment): boolean => {
  return isDangerSign(assessment.name)
    && assessment.severity === 'risk_present'
    && assessment.triage_recommendation === 'urgent';
};

/*  A condition the clinical logic actually detected: it carries a real risk severity. */
export const isDetectedRisk = (assessment: { severity: string }): boolean => {
  return RISK_SEVERITIES.includes(assessment.severity);
};

/*  A danger sign the clinical logic detected. Its care plan is never optional: an emergency
    referral cannot be left off the screen because a box was left unticked. */
export const isDetectedDangerSign = (assessment: { name: string; severity: string }): boolean => {
  return isDangerSign(assessment.name) && isDetectedRisk(assessment);
};

/*  The conditions the care plan has to cover.

    The worker's own selection is what decides: a ticked condition gets its care plan, an
    unticked one does not. The one exception is a danger sign, which always comes along with
    its referral, whatever else was ticked. */
export const carePlanScope = (
  assessments: ScoredAssessment[],
  selected: string[],
): string[] => {
  const scope = new Set(selected);

  assessments.filter(isDetectedDangerSign).forEach((assessment) => scope.add(assessment.name));

  return [...scope];
};

/*  Did the worker pick a condition of their own?

    The danger signs are ticked by the clinical logic, not by the worker, so they cannot be
    read as a choice. When the worker has picked nothing of their own the screen has not been
    narrowed down yet and every detected condition still belongs in the care plan. */
export const hasWorkerSelection = (selected: string[]): boolean => {
  return selected.some(name => !isDangerSign(name));
};
